import { z } from 'zod';

/**
 * Mirrors `PrismaEnums.TeacherAttendancePolicyActionLevel` without importing
 * `@airreading/common` (its barrel pulls `@airreading/common/data` and hits a
 * coach-payment circular import under Vitest `@airreading/source`).
 */
export const TeacherAttendancePolicyActionLevel = {
  within_allowance: 'within_allowance',
  final_warning: 'final_warning',
  reassignment_eligible: 'reassignment_eligible',
  third_no_show: 'third_no_show',
  third_late_entry: 'third_late_entry',
  third_early_exit: 'third_early_exit',
  final_14_day_protected: 'final_14_day_protected',
} as const;
export type TeacherAttendancePolicyActionLevel =
  (typeof TeacherAttendancePolicyActionLevel)[keyof typeof TeacherAttendancePolicyActionLevel];

type AttendancePolicyV2OverviewUsage = {
  totalSubRequestUsage: number;
  totalSubRequestAllowance: number;
  shortNoticeSubRequestUsage: number;
  shortNoticeSubRequestAllowance: number;
};

type AttendancePolicyV2HistoryEventKind =
  | 'no_show'
  | 'late_entry'
  | 'early_exit'
  | 'regular_sub_request'
  | 'short_notice_sub_request'
  | 'exemption'
  | 'technical_failure';

export function formatAttendancePolicyV2Limit({
  totalSubRequestAllowance,
  shortNoticeSubRequestAllowance,
}: {
  totalSubRequestAllowance: number;
  shortNoticeSubRequestAllowance: number;
}): string {
  const classLabel = totalSubRequestAllowance === 1 ? 'class' : 'classes';
  return `${totalSubRequestAllowance} ${classLabel} (max ${shortNoticeSubRequestAllowance} short-notice)`;
}

export function formatAttendancePolicyV2UsageFraction(usage: number, allowance: number): string {
  return `${usage} / ${allowance}`;
}

export function formatAttendancePolicyV2OverviewLines(policy: AttendancePolicyV2OverviewUsage): {
  totalLine: string;
  shortNoticeLine: string;
} {
  const { totalSubRequestUsage, totalSubRequestAllowance, shortNoticeSubRequestUsage, shortNoticeSubRequestAllowance } =
    policy;

  return {
    totalLine: `${formatAttendancePolicyV2UsageFraction(totalSubRequestUsage, totalSubRequestAllowance)} total`,
    shortNoticeLine: `${formatAttendancePolicyV2UsageFraction(
      shortNoticeSubRequestUsage,
      shortNoticeSubRequestAllowance,
    )} short notice`,
  };
}

export const AttendancePolicyV2ActionLevelColorMap: Record<TeacherAttendancePolicyActionLevel, string> = {
  [TeacherAttendancePolicyActionLevel.within_allowance]: 'bg-[#DCFCE7] hover:bg-[#DCFCE7] text-[#166534]',
  [TeacherAttendancePolicyActionLevel.final_warning]: 'bg-[#FFEDD5] hover:bg-[#FFEDD5] text-[#EA580C]',
  [TeacherAttendancePolicyActionLevel.reassignment_eligible]: 'bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#DC2626]',
  [TeacherAttendancePolicyActionLevel.third_no_show]: 'bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#DC2626]',
  [TeacherAttendancePolicyActionLevel.third_late_entry]: 'bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#DC2626]',
  [TeacherAttendancePolicyActionLevel.third_early_exit]: 'bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#DC2626]',
  [TeacherAttendancePolicyActionLevel.final_14_day_protected]: 'bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#DC2626]',
};

export const AttendancePolicyV2ActionLevelLabelMap: Record<TeacherAttendancePolicyActionLevel, string> = {
  [TeacherAttendancePolicyActionLevel.within_allowance]: 'Within allowance',
  [TeacherAttendancePolicyActionLevel.final_warning]: 'Final warning',
  [TeacherAttendancePolicyActionLevel.reassignment_eligible]: 'Action required',
  [TeacherAttendancePolicyActionLevel.third_no_show]: 'Action required',
  [TeacherAttendancePolicyActionLevel.third_late_entry]: 'Action required',
  [TeacherAttendancePolicyActionLevel.third_early_exit]: 'Action required',
  [TeacherAttendancePolicyActionLevel.final_14_day_protected]: 'Action required',
};

/** Ops filter groups aligned with badge labels (not every raw actionLevel). */
export const attendancePolicyV2ActionLevelFilterGroupSchema = z.enum([
  TeacherAttendancePolicyActionLevel.within_allowance,
  TeacherAttendancePolicyActionLevel.final_warning,
  'action_required',
]);
export type AttendancePolicyV2ActionLevelFilterGroup = z.infer<typeof attendancePolicyV2ActionLevelFilterGroupSchema>;
export const AttendancePolicyV2ActionLevelFilterGroup = attendancePolicyV2ActionLevelFilterGroupSchema.enum;

export const AttendancePolicyV2ActionLevelFilterGroupLabelMap: Record<
  AttendancePolicyV2ActionLevelFilterGroup,
  string
> = {
  [AttendancePolicyV2ActionLevelFilterGroup.within_allowance]: 'Within allowance',
  [AttendancePolicyV2ActionLevelFilterGroup.final_warning]: 'Final warning',
  [AttendancePolicyV2ActionLevelFilterGroup.action_required]: 'Action required',
};

export const AttendancePolicyV2ActionLevelFilterGroupToActionLevels: Record<
  AttendancePolicyV2ActionLevelFilterGroup,
  TeacherAttendancePolicyActionLevel[]
> = {
  [AttendancePolicyV2ActionLevelFilterGroup.within_allowance]: [TeacherAttendancePolicyActionLevel.within_allowance],
  [AttendancePolicyV2ActionLevelFilterGroup.final_warning]: [TeacherAttendancePolicyActionLevel.final_warning],
  [AttendancePolicyV2ActionLevelFilterGroup.action_required]: [
    TeacherAttendancePolicyActionLevel.reassignment_eligible,
    TeacherAttendancePolicyActionLevel.third_no_show,
    TeacherAttendancePolicyActionLevel.third_late_entry,
    TeacherAttendancePolicyActionLevel.third_early_exit,
    TeacherAttendancePolicyActionLevel.final_14_day_protected,
  ],
};

export const attendancePolicyV2ActionLevelFilterGroupOptions = (
  Object.keys(AttendancePolicyV2ActionLevelFilterGroupLabelMap) as AttendancePolicyV2ActionLevelFilterGroup[]
).map((value) => ({
  label: AttendancePolicyV2ActionLevelFilterGroupLabelMap[value],
  value,
}));

export function expandAttendancePolicyV2ActionLevelFilterGroups(
  groups: AttendancePolicyV2ActionLevelFilterGroup[] | undefined,
): TeacherAttendancePolicyActionLevel[] | undefined {
  if (!groups?.length) {
    return undefined;
  }

  return [...new Set(groups.flatMap((group) => AttendancePolicyV2ActionLevelFilterGroupToActionLevels[group]))];
}

export const AttendancePolicyV2HistoryEventBadgeColorMap: Record<AttendancePolicyV2HistoryEventKind, string> = {
  no_show: 'bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#DC2626]',
  late_entry: 'bg-[#FFEDD5] hover:bg-[#FFEDD5] text-[#EA580C]',
  early_exit: 'bg-[#FFEDD5] hover:bg-[#FFEDD5] text-[#EA580C]',
  regular_sub_request: 'bg-[#DBEAFE] hover:bg-[#DBEAFE] text-[#1D4ED8]',
  short_notice_sub_request: 'bg-[#DBEAFE] hover:bg-[#DBEAFE] text-[#1D4ED8]',
  exemption: 'bg-[#DCFCE7] hover:bg-[#DCFCE7] text-[#166534]',
  technical_failure: 'bg-[#DCFCE7] hover:bg-[#DCFCE7] text-[#166534]',
};

export const AttendancePolicyV2HistoryEventDotColorMap: Record<AttendancePolicyV2HistoryEventKind, string> = {
  no_show: 'bg-[#DC2626]',
  late_entry: 'bg-[#EA580C]',
  early_exit: 'bg-[#EA580C]',
  regular_sub_request: 'bg-[#1D4ED8]',
  short_notice_sub_request: 'bg-[#1D4ED8]',
  exemption: 'bg-[#166534]',
  technical_failure: 'bg-[#166534]',
};
